import { EventEmitter } from '@angular/core';
import { IKanbanDataProvider } from './interfaces/kanban-data-provider.interface';
import {
  BatchActionEvent,
  BatchSelectionChangeEvent,
  KanbanBatchAction,
  KanbanCard,
  KanbanColumn,
  SelectAllInColumnEvent,
  SelectionPruneEvent,
} from './models/kanban.model';
import { filterKanbanColumns } from './pipes/kanban-filter.pipe';

export interface KanbanSelectionHost {
  columns: KanbanColumn[];
  searchQuery: string;
  enableSelection: boolean;
  selectedCardIds: Array<string | number>;
  selectionColumnId: string | number | null;
  processingBatch: boolean;
  provider: IKanbanDataProvider;
  selectionChange: EventEmitter<BatchSelectionChangeEvent>;
  selectAllInColumn: EventEmitter<SelectAllInColumnEvent>;
  batchAction: EventEmitter<BatchActionEvent>;
  selectionPrune: EventEmitter<SelectionPruneEvent>;
  selectionCancel: EventEmitter<void>;
}

export class KanbanBoardSelection {
  constructor(private readonly host: KanbanSelectionHost) {}

  isSelectionActive(): boolean {
    return this.host.enableSelection && this.host.selectedCardIds.length > 0;
  }

  get activeSelectionColumn(): KanbanColumn | null {
    const column = this.findSelectedColumn();
    if (!column) {
      return null;
    }

    const visible = this.toVisibleColumn(column);
    return this.hasVisibleSelection(visible) ? visible : null;
  }

  isSelectionActiveForColumn(column: KanbanColumn): boolean {
    return this.isSelectionActive() && `${this.host.selectionColumnId}` === `${column.id}`;
  }

  isCardSelectable(card: KanbanCard): boolean {
    if (!this.host.enableSelection) {
      return false;
    }
    if (this.host.provider?.isCardSelectable) {
      return this.host.provider.isCardSelectable(card);
    }
    return true;
  }

  getCardSelectionDenyReason(card: KanbanCard, column: KanbanColumn): string | null {
    if (!this.host.provider?.getCardSelectionDenyReason) {
      return null;
    }
    return this.host.provider.getCardSelectionDenyReason(card, column);
  }

  isCardSelectionDisabled(card: KanbanCard, column: KanbanColumn): boolean {
    return !!this.getCardSelectionDenyReason(card, column);
  }

  isCardCheckboxVisible(card: KanbanCard, column: KanbanColumn): boolean {
    return this.canSelectInColumn(column) && this.isCardSelectable(card);
  }

  isCardCheckboxEnabled(card: KanbanCard, column: KanbanColumn): boolean {
    return this.isCardCheckboxVisible(card, column) && !this.isCardSelectionDisabled(card, column);
  }

  isCardSelected(card: KanbanCard): boolean {
    return this.host.selectedCardIds.some(id => `${id}` === `${card.id}`);
  }

  getCardSelectionAriaLabel(card: KanbanCard): string {
    if (this.host.provider?.getCardSelectionLabel) {
      return this.host.provider.getCardSelectionLabel(card);
    }
    return `Selecionar ${card.title}`;
  }

  getCardSelectionDenyElementId(card: KanbanCard): string {
    return `kanban-card-deny-${card.id}`;
  }

  getCardSelectionDescribedBy(card: KanbanCard, column: KanbanColumn): string | null {
    if (!this.getVisibleSelectionDenyReason(card, column)) {
      return null;
    }
    return this.getCardSelectionDenyElementId(card);
  }

  getVisibleSelectionDenyReason(card: KanbanCard, column: KanbanColumn): string | null {
    if (!this.isCardCheckboxVisible(card, column)) {
      return null;
    }
    return this.getCardSelectionDenyReason(card, column);
  }

  getVisibleSelectedCount(column: KanbanColumn): number {
    const visibleIds = new Set(column.cards.map(card => `${card.id}`));
    return this.host.selectedCardIds.filter(id => visibleIds.has(`${id}`)).length;
  }

  getBatchActions(column: KanbanColumn): KanbanBatchAction[] {
    if (!this.host.provider?.getBatchActions) {
      return [];
    }
    const selectedCards = column.cards.filter(card => this.isCardSelected(card));
    return this.host.provider.getBatchActions(column, selectedCards);
  }

  getSelectableCardCount(column: KanbanColumn): number {
    return column.cards.filter(card => this.isCardCheckboxEnabled(card, column)).length;
  }

  onCardSelectionToggle(card: KanbanCard, column: KanbanColumn, event: Event): void {
    event.stopPropagation();

    if (this.host.processingBatch || !this.isCardCheckboxEnabled(card, column)) {
      return;
    }

    const input = event.target as HTMLInputElement;
    this.host.selectionChange.emit({
      columnId: column.id,
      cardId: card.id,
      selected: input.checked,
    });
  }

  onSelectAllInColumn(column: KanbanColumn): void {
    if (this.host.processingBatch) {
      return;
    }

    const visibleCardIds = column.cards
      .filter(card => this.isCardCheckboxEnabled(card, column))
      .map(card => card.id);
    this.host.selectAllInColumn.emit({ columnId: column.id, visibleCardIds });
  }

  onBatchAction(column: KanbanColumn, action: KanbanBatchAction): void {
    if (this.host.processingBatch || action.disabled || !this.isSelectionActiveForColumn(column)) {
      return;
    }

    const visibleIds = new Set(column.cards.map(card => `${card.id}`));
    const cardIds = this.host.selectedCardIds.filter(id => visibleIds.has(`${id}`));
    this.host.batchAction.emit({ actionId: action.id, columnId: column.id, cardIds });
  }

  onBatchCancel(): void {
    if (this.host.processingBatch) {
      return;
    }
    this.host.selectionCancel.emit();
  }

  emitVisibleSelection(): void {
    if (this.host.selectionColumnId === null || this.host.selectedCardIds.length === 0) {
      return;
    }

    const column = this.host.columns.find(col => `${col.id}` === `${this.host.selectionColumnId}`);
    const visible = column ? this.toVisibleColumn(column) : undefined;
    this.host.selectionPrune.emit({
      columnId: this.host.selectionColumnId,
      visibleCardIds: visible?.cards.map(card => card.id) ?? [],
    });
  }

  private canSelectInColumn(column: KanbanColumn): boolean {
    if (!this.host.enableSelection) {
      return false;
    }
    if (!this.host.provider?.isColumnSelectable) {
      return true;
    }
    return this.host.provider.isColumnSelectable(column);
  }

  private findSelectedColumn(): KanbanColumn | null {
    if (!this.isSelectionActive() || this.host.selectionColumnId === null) {
      return null;
    }
    return this.host.columns.find(col => `${col.id}` === `${this.host.selectionColumnId}`) ?? null;
  }

  private toVisibleColumn(column: KanbanColumn): KanbanColumn {
    return filterKanbanColumns([column], this.host.searchQuery)[0] ?? column;
  }

  private hasVisibleSelection(column: KanbanColumn): boolean {
    const visibleIds = new Set(column.cards.map(card => `${card.id}`));
    return this.host.selectedCardIds.some(id => visibleIds.has(`${id}`));
  }
}
